import { AlertCircle, RotateCcw, RefreshCw } from 'lucide-react';

/**
 * Global app-level error fallback — replaces entire screen.
 * Used by the root ErrorBoundary in main.jsx.
 */
export function AppErrorFallback({ error }) {
  return (
    <div className="flex h-dvh w-full items-center justify-center bg-background text-foreground">
      <div className="text-center max-w-md px-6">
        <AlertCircle size={48} className="text-destructive mx-auto mb-4" />
        <h1 className="text-xl font-semibold mb-2">Something went wrong</h1>
        <p className="text-muted-foreground mb-6">
          An unexpected error occurred. Your data is safe — please reload to continue.
        </p>
        {import.meta.env.DEV && error?.message && (
          <pre className="text-xs text-left bg-muted p-3 rounded-md mb-6 overflow-auto max-h-32">
            {error.message}
          </pre>
        )}
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <RefreshCw size={16} />
          Reload App
        </button>
      </div>
    </div>
  );
}

/**
 * Editor-level error fallback — renders inside the editor column.
 * Used by the ErrorBoundary wrapping MainEditor in App.jsx.
 * Supports resetErrorBoundary for retry without full reload.
 */
export function EditorErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div className="flex flex-1 items-center justify-center bg-background text-foreground">
      <div className="text-center max-w-sm px-6">
        <AlertCircle size={36} className="text-destructive mx-auto mb-3" />
        <h2 className="text-lg font-semibold mb-2">Editor error</h2>
        <p className="text-sm text-muted-foreground mb-4">
          This note failed to render. Try selecting another note or retry.
        </p>
        {import.meta.env.DEV && error?.message && (
          <pre className="text-xs text-left bg-muted p-3 rounded-md mb-4 overflow-auto max-h-24">
            {error.message}
          </pre>
        )}
        <button
          onClick={resetErrorBoundary}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm"
        >
          <RotateCcw size={14} />
          Try Again
        </button>
      </div>
    </div>
  );
}
